/** Which of this workspace's figures one library factor priced - the factor page's "Used by".
 *
 * `GET /api/v1/factors/usage`, in its own file rather than in `factors.ts`: that module is the
 * library (global reference data, no mutation, an export list a test pins), and this answer is
 * **tenant data** - it reads the workspace's calculations. The server names the factor by the five
 * parts that identify it in the library and counts exactly; nothing here infers a use from a fuel
 * name.
 *
 * Never succeeds with nothing: a failed read throws, so "no figure was priced with this factor"
 * (a real answer, counts of zero) can never be what a dropped request looks like. Counts are
 * integers and are left as numbers; each row's amounts are the server's decimal strings (rule 5).
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type FactorUsage = components["schemas"]["FactorUsageOut"];
export type FactorUsageRow = components["schemas"]["FactorUsageRowOut"];

/** The five parts that identify a factor in the library (its unique constraint). */
export interface FactorParts {
  set_key: string;
  category: string;
  fuel_or_carrier: string;
  region: string;
  unit_in: string;
}

export const FACTOR_USAGE_LIMIT = 10;

export function useFactorUsage(parts: FactorParts | undefined) {
  return useQuery({
    queryKey: ["factor-usage", parts?.set_key, parts?.category, parts?.fuel_or_carrier, parts?.region, parts?.unit_in] as const,
    enabled: parts !== undefined,
    retry: false,
    // A figure recorded a minute ago is what the reader came to see; a cached count is the stale
    // fact the library page itself refuses to hold.
    staleTime: 0,
    queryFn: async (): Promise<FactorUsage> => {
      if (!parts) throw new Error("No factor was named.");
      const { data, error, response } = await api.GET("/api/v1/factors/usage", {
        params: { query: { ...parts, limit: FACTOR_USAGE_LIMIT } },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "Which figures this factor priced could not be read.");
      }
      return data;
    },
  });
}
